import { foldLexicalToken } from './serp-intent-highlight';
import type { DomainTopicUrlRow } from './topic-supporting-pages';
import {
  filterRowsByFocalAnchorTopics,
  filterYourMatchPoolForCompetitorRow,
  normalizeUrlForCompare
} from './topic-supporting-pages';

/** Min Jaccard overlap (folded tokens) to show “Similar on your site”. */
export const SUPPORTING_PAGE_SIMILARITY_MIN_SCORE = 0.22;

export interface PageLexicalFields {
  title: string;
  h1: string;
}

export interface SupportingPageSimilarMatch {
  url: string;
  score: number;
}

const STOPWORDS = new Set([
  'your', 'the', 'and', 'for', 'with', 'that', 'this', 'from', 'than', 'into', 'over',
  'after', 'all', 'who', 'can', 'been', 'have', 'more', 'when', 'what', 'how', 'are',
  'was', 'were', 'will', 'would', 'could', 'should', 'about', 'blog', 'www', 'com', 'https', 'http'
]);

function addTokensFromText(text: string, out: Set<string>): void {
  const parts = (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-/]/g, ' ')
    .split(/[\s-/]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);
  for (const part of parts) {
    const fold = foldLexicalToken(part);
    if (fold.length < 3 || STOPWORDS.has(fold)) continue;
    out.add(fold);
  }
}

/** Token set for one supporting-page row (path + topics + stored title/H1 when available). */
export function buildSupportingPageTokenSet(
  row: DomainTopicUrlRow,
  lexical?: PageLexicalFields | null
): Set<string> {
  const tokens = new Set<string>();
  try {
    const parsed = new URL(row.url);
    addTokensFromText(parsed.pathname, tokens);
  } catch {
    addTokensFromText(row.url, tokens);
  }
  for (const chip of row.topics) {
    addTokensFromText(chip.topicLabel, tokens);
    addTokensFromText(chip.topicSlug.replace(/-/g, ' '), tokens);
  }
  if (lexical?.title) addTokensFromText(lexical.title, tokens);
  if (lexical?.h1) addTokensFromText(lexical.h1, tokens);
  return tokens;
}

export function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let intersection = 0;
  for (const t of a) {
    if (b.has(t)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union > 0 ? intersection / union : 0;
}

export function findBestSimilarYourPage(
  competitorRow: DomainTopicUrlRow,
  yourRows: DomainTopicUrlRow[],
  lexicalByUrl: Map<string, PageLexicalFields>,
  minScore = SUPPORTING_PAGE_SIMILARITY_MIN_SCORE
): SupportingPageSimilarMatch | null {
  if (!yourRows.length) return null;
  const competitorTokens = buildSupportingPageTokenSet(
    competitorRow,
    lexicalByUrl.get(normalizeUrlForCompare(competitorRow.url)) || null
  );
  if (!competitorTokens.size) return null;

  let best: SupportingPageSimilarMatch | null = null;
  for (const yourRow of yourRows) {
    if (normalizeUrlForCompare(yourRow.url) === normalizeUrlForCompare(competitorRow.url)) {
      continue;
    }
    const yourTokens = buildSupportingPageTokenSet(
      yourRow,
      lexicalByUrl.get(normalizeUrlForCompare(yourRow.url)) || null
    );
    const score = jaccardSimilarity(competitorTokens, yourTokens);
    if (score < minScore) continue;
    if (!best || score > best.score) {
      best = { url: yourRow.url, score };
    }
  }
  return best;
}

export function collectSupportingPageUrls(domains: Array<{ rows: DomainTopicUrlRow[] }>): string[] {
  const urls = new Set<string>();
  for (const block of domains) {
    for (const row of block.rows) {
      if (row.url) urls.add(row.url);
    }
  }
  return [...urls];
}

/** Human-readable summary of the token set used for v1 Jaccard (debug + opportunity panel). */
export function formatSupportingPageTokenPreview(
  row: DomainTopicUrlRow,
  lexical?: PageLexicalFields | null
): string {
  const tokens = [...buildSupportingPageTokenSet(row, lexical)].sort();
  const lines = [
    'v1 Jaccard — folded tokens from URL path, topic chips, title, and H1.',
    `Token count: ${tokens.length}`,
    tokens.length > 0 ? `Tokens: ${tokens.join(', ')}` : '(no tokens extracted)'
  ];
  return lines.join('\n');
}

export function buildLexicalByNormalizedUrl(
  byUrl: Record<string, PageLexicalFields>
): Map<string, PageLexicalFields> {
  const map = new Map<string, PageLexicalFields>();
  for (const [url, fields] of Object.entries(byUrl || {})) {
    map.set(normalizeUrlForCompare(url), fields);
  }
  return map;
}

export interface TopicOwnershipDomainLike {
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

export interface SupportingPageSimilarityOptions {
  /** When set, “your site” pool is limited to rows with matching topic chips (focal anchor). */
  focalAnchorSlugs?: Set<string>;
  /** Also require shared topic chip between competitor row and your candidate. */
  perRowTopicOverlap?: boolean;
}

/** Attach `similarOnYourSite` on competitor rows (mutates rows). */
export function attachSupportingPageSimilarityMatches(
  domains: TopicOwnershipDomainLike[],
  lexicalByUrl: Map<string, PageLexicalFields>,
  options?: SupportingPageSimilarityOptions
): void {
  const yours = domains.find((d) => d.isYourSite);
  if (!yours?.rows.length) return;

  const anchorSlugs = options?.focalAnchorSlugs;
  const defaultPool = anchorSlugs?.size
    ? filterRowsByFocalAnchorTopics(yours.rows, anchorSlugs)
    : yours.rows;

  for (const block of domains) {
    if (block.isYourSite) continue;
    for (const row of block.rows) {
      let pool = defaultPool;
      if (options?.perRowTopicOverlap && anchorSlugs?.size) {
        const narrowed = filterYourMatchPoolForCompetitorRow(yours.rows, row, anchorSlugs);
        if (narrowed.length) pool = narrowed;
      }
      row.similarOnYourSite = findBestSimilarYourPage(row, pool, lexicalByUrl) || undefined;
    }
  }
}
